import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import { tokenStore } from './auth/token-store';
import { ToastProvider } from './components/ui/toast';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Don't retry 4xx — those are client errors that won't fix themselves.
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element missing');

// Bounce to /login the moment the token store clears (e.g. after a failed
// Phase A U3: admin impersonation handoff. The console opens this app with
// a 30-minute access token in the URL FRAGMENT (never a query param —
// fragments don't reach server logs). No refresh token rides along: the
// box ends itself, and a tab refresh ends it early (access tokens are
// memory-only). Any existing session in this browser is cleared first so
// expiry can never silently fall back to the admin's own account.
const impMatch = /[#&]imp=([\w.~+/=-]+)/.exec(window.location.hash);
if (impMatch?.[1]) {
  tokenStore.clear();
  tokenStore.setAccess(impMatch[1], new Date(Date.now() + 30 * 60_000).toISOString());
  history.replaceState(null, '', window.location.pathname);
}

// refresh while the user is on a protected page). The router's beforeLoad
// gates wouldn't fire until next navigation otherwise.
tokenStore.subscribe(() => {
  if (!tokenStore.isAuthenticated() && router.state.location.pathname !== '/login') {
    router.navigate({ to: '/login' });
  }
});

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
